import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds , MobileUnderCalAds } from "../../Ads";
const TITLE = "Calculatrice de masse molaire - Apprenez à calculer la masse molaire facilement";
const DESCRIPTION =
  "Calculatrice de masse molaire - Apprenez à calculer la masse molaire facilement. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "molar mass calculator, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/molar-mass-calculator/";
const elements = [
  { symbol: "H", name: "Hydrogène", molarMass: 1.0079 },
  { symbol: "C", name: "Carbone", molarMass: 12.011 },
  { symbol: "N", name: "Azote", molarMass: 14.007 },
  { symbol: "O", name: "Oxygène", molarMass: 15.999 },
  { symbol: "S", name: "Soufre", molarMass: 32.06 },
  { symbol: "P", name: "Phosphore", molarMass: 30.974 },
  { symbol: "Cl", name: "Chlore", molarMass: 35.45 },
  { symbol: "Na", name: "Sodium", molarMass: 22.99 },
  { symbol: "K", name: "Potassium", molarMass: 39.098 },
  { symbol: "Ca", name: "Calcium", molarMass: 40.078 },
  { symbol: "Mg", name: "Magnésium", molarMass: 24.305 },
  { symbol: "Fe", name: "Fer", molarMass: 55.845 },
  { symbol: "Cu", name: "Cuivre", molarMass: 63.546 },
  { symbol: "Zn", name: "Zinc", molarMass: 65.38 },
  { symbol: "Al", name: "Aluminium", molarMass: 26.982 },
];

function Calculator() {
  const [compound, setCompound] = useState([{ element: "", atoms: 1 }]);
  const [molarMass, setMolarMass] = useState("");
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState("");

  const handleAddElement = () => {
    setCompound([...compound, { element: "", atoms: 1 }]);
  };

  const handleElementChange = (index, value) => {
    const newCompound = [...compound];
    newCompound[index].element = value;
    setCompound(newCompound);
  };

  const handleAtomChange = (index, value) => {
    const newCompound = [...compound];
    newCompound[index].atoms = value;
    setCompound(newCompound);
  };

  const handleCalculate = () => {
    let totalMolarMass = 0;
    let explanationSteps = "";
    for (const { element, atoms } of compound) {
      if (!element) {
        setError("Veuillez entrer tous les éléments et le nombre d'atomes correctement");
        setMolarMass("");
        setExplanation("");
        return;
      }
      const elementData = elements.find((e) => e.symbol === element);
      if (!elementData) {
        setError("Élément invalide. Veuillez choisir un élément valide.");
        setMolarMass("");
        setExplanation("");
        return;
      }
      const elementMolarMass = elementData.molarMass * atoms;
      totalMolarMass += elementMolarMass;
      explanationSteps += `${atoms} × ${elementData.molarMass} (masse molaire de ${elementData.name}) = ${elementMolarMass.toFixed(2)} g/mol\n`;
    }
    setMolarMass(totalMolarMass.toFixed(2));
    setExplanation(explanationSteps);
    setError("");
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  La masse molaire est la masse d'une mole d'un élément ou d'un composé 
                  chimique. Pour calculer la masse molaire d'un composé donné, vous devez 
                  additionner les masses molaires de tous les atomes présents dans le 
                  composé. La masse molaire d'un élément se trouve dans le tableau périodique 
                  et est exprimée en g/mol. Par exemple, pour calculer la masse molaire de 
                  l'eau (H2O):
                </p>
                <ul>
                  <li>Masse molaire de l'hydrogène = 1.008 g/mol</li>
                  <li>Masse molaire de l'oxygène = 16.00 g/mol</li>
                  <li>Masse molaire de l'eau = (2 × 1.008) + 16.00 = 18.016 g/mol</li>
                </ul>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div>
                  {compound.map((item, index) => (
                    <div key={index} className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label" style={{marginRight: "10px"}}>
                          Élément {index + 1}
                        </label>
                      </b>
                      <select
                        value={item.element}
                        onChange={(e) => handleElementChange(index, e.target.value)}
                        className="form-control"
                        style={{ width: "150px", marginRight: "10px" }}
                      >
                        <option value="">Choisir l'élément</option>
                        {elements.map((el) => (
                          <option key={el.symbol} value={el.symbol}>
                            {el.name} ({el.symbol})
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        placeholder="Nombre d'atomes"
                        value={item.atoms}
                        onChange={(e) => handleAtomChange(index, parseInt(e.target.value))}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  ))}
                  <br />
                  <button className="btn btn-success" onClick={handleAddElement}>
                    Ajouter un autre élément
                  </button>
                  <br/>
                  <button className="btn btn-success" style={{ fontSize: "20px" }} onClick={handleCalculate}>
                    Calculer
                  </button>
                </div>
                <br />
                <div>
                  {error !== "" ? (
                    <p className="p-3 mb-2 bg-warning text-white">Erreur: {error}</p>
                  ) : (
                    <>
                      {molarMass !== "" && (
                        <>
                          <p className="p-3 mb-2 bg-success text-white">
                            Masse molaire (g/mol): {molarMass}
                          </p>
                          {explanation !== "" && (
                            <p className="p-3 mb-2 bg-info text-black">
                              Étapes de calcul: <br />
                              {explanation.split("\n").map((line, index) => (
                                <React.Fragment key={index}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))}
                            </p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
        </div>
      </div>
    </div>
  );
}
export default Calculator;
